import type { OverlayBundle } from "../api/resources";

/** A phase and the frame the render drew it on. */
export interface BundlePosition {
  label: string;
  fileId: number;
}

/**
 * The upload's phases, taken from the overlay bundle the RENDER wrote.
 *
 * Each overlay in the bundle carries the P-position it was drawn for, so the bundle already pairs every
 * phase with its frame — and it is a file read. The alternative, getPPositionFrames, re-runs the position
 * finder against the database: on a clip of a few hundred frames that can take minutes and time out, and
 * while it does the live view has no phases to offer at all. It can also land on a different frame than
 * the render used, in which case the page's phase would not be the phase on screen.
 *
 * One entry per phase — the first frame carrying it, which for a repeating position (every rep's bottom)
 * is the first rep, the same one the still shows — ordered by phase number so P10 follows P9.
 */
export function positionsFromBundle(bundle: OverlayBundle | undefined | null): BundlePosition[] {
  const firstFrameFor = new Map<string, number>();
  for (const frame of bundle?.frames ?? []) {
    for (const overlay of frame.overlays ?? []) {
      const label = overlay.position;
      if (!label || firstFrameFor.has(label)) continue;
      firstFrameFor.set(label, frame.fileId);
    }
  }
  const phaseNumber = (label: string) => {
    const n = Number(label.replace(/^P/i, ""));
    return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
  };
  const entries: BundlePosition[] = [];
  firstFrameFor.forEach((fileId, label) => entries.push({ label, fileId }));
  return entries
    .sort((a, b) => phaseNumber(a.label) - phaseNumber(b.label));
}
